'use client';

import React, { useCallback, useContext, useState } from 'react';
import { useModalState } from '@/hooks/useModalState';

export enum ModalTypes {
  ACCESS_RESTRICTED = 'ACCESS_RESTRICTED',
}

type ModalType = keyof typeof ModalTypes;

type SystemModalsContextType = {
  modalType: ModalType | null;
  isOpen: boolean;
  onOpenModal: (modalType: ModalType) => void;
  onCloseModal: () => void;
};

const SystemModalsContext = React.createContext({} as SystemModalsContextType);

interface Props {
  children: React.ReactNode;
}

export const SystemModalsProvider = ({ children }: Props) => {
  const [modalType, setModalType] = useState<ModalType | null>(null);
  const { isOpen, open, close } = useModalState();

  const onOpenModal = useCallback(
    (modalType: ModalType) => {
      setModalType(modalType);
      open();
    },
    [open]
  );

  const onCloseModal = useCallback(() => {
    setModalType(null);
    close();
  }, [close]);

  return (
    <SystemModalsContext.Provider
      value={{
        isOpen,
        modalType,
        onOpenModal,
        onCloseModal,
      }}
    >
      {children}
    </SystemModalsContext.Provider>
  );
};

export const useSystemModals = () => {
  return useContext(SystemModalsContext);
};
